import { useState, useEffect, ChangeEvent } from "react";
import { Dispatch, SetStateAction } from "react";
import {
  Modal,
  Button,
  Select,
  MenuItem,
  TextField,
  IconButton,
  CircularProgress,
  CardMedia,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { useUploadMediaMutation } from "../service/common/api";
import {
  useAddCategoryBannerMutation,
  useGetCategoryBannerByIdQuery,
  useUpdateCategoryBannerMutation,
} from "../service/cms/api";
import { useGetCategoriesQuery } from "../service/features/api";
import { VEHICLE_TYPE } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  editId?: string | null;
}

export default function AddCategoryBanner({
  open,
  onClose,
  setOpen,
  editId,
}: ModalProps) {
  const isEdit = !!editId;

  const [vehicleType, setVehicleType] = useState<number | "">("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [categoryName, setCategoryName] = useState<string>("");
  const [image, setImage] = useState("");
  const [errors, setErrors] = useState<{
    vehicleType?: string;
    categoryId?: string;
    image?: string;
  }>({});

  const [uploadMedia, { isLoading: isUploading }] = useUploadMediaMutation();
  const [addCategoryBanner, { isLoading: isAdding }] = useAddCategoryBannerMutation();
  const [updateCategoryBanner, { isLoading: isUpdating }] = useUpdateCategoryBannerMutation();
  const isSubmitting = isAdding || isUpdating;

  const { data: categoriesData } = useGetCategoriesQuery(
    { type: vehicleType || undefined, page: 1, limit: 100 },
    { skip: !open || vehicleType === "" }
  );
  const categories: any[] = categoriesData?.data?.data ?? [];

  const { data: editData, isFetching: isFetchingEdit } = useGetCategoryBannerByIdQuery(
    editId as string,
    { skip: !open || !editId }
  );

  useEffect(() => {
    if (!open) return;
    if (isEdit && editData?.data) {
      setVehicleType(editData.data.type ?? "");
      setCategoryId(editData.data.categoryId ?? "");
      setCategoryName(editData.data.category?.name ?? "");
      setImage(editData.data.image ?? "");
      setErrors({});
    } else if (!isEdit) {
      setVehicleType("");
      setCategoryId("");
      setCategoryName("");
      setImage("");
      setErrors({});
    }
  }, [open, isEdit, editData]);

  // Reset category selection when vehicle type changes (add mode only)
  useEffect(() => {
    if (!isEdit) setCategoryId("");
  }, [vehicleType, isEdit]);

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await uploadMedia(formData).unwrap();
      const imageUrl = res?.data?.url || res?.url || res?.data;
      if (imageUrl) {
        setImage(imageUrl);
        if (errors.image) setErrors((prev) => ({ ...prev, image: undefined }));
        toast.success("Image uploaded successfully");
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Image upload failed");
    }
  };

  const handleSave = async () => {
    const newErrors: typeof errors = {};
    if (!vehicleType) newErrors.vehicleType = "Vehicle type is required";
    if (!categoryId) newErrors.categoryId = "Category is required";
    if (!image) newErrors.image = "Image is required";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      if (isEdit) {
        const res = await updateCategoryBanner({ id: editId, image }).unwrap();
        toast.success(res?.message || "Category banner updated successfully");
      } else {
        const res = await addCategoryBanner({
          image,
          type: vehicleType as number,
          categoryId,
          isActive: true,
        }).unwrap();
        toast.success(res?.message || "Category banner added successfully");
      }
      setOpen(false);
    } catch (err: any) {
      toast.error(
        err?.data?.message ||
          `Failed to ${isEdit ? "update" : "add"} category banner`
      );
    }
  };

  return (
    <Modal
      className="modal addNotification_modal"
      open={open}
      onClose={() => {
        onClose();
        setErrors({});
      }}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2 style={{ whiteSpace: "nowrap" }}>
              {isEdit ? "Edit Category Banner" : "Add Category Banner"}
            </h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </button>
          </div>

          {isFetchingEdit ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "40px 0",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <form className="form">
              <div className="control_group">
                <label>Vehicle Type</label>
                {isEdit ? (
                  <TextField
                    fullWidth
                    hiddenLabel
                    value={vehicleType !== "" ? getVehicleTypeLabel(vehicleType) : ""}
                    disabled
                  />
                ) : (
                  <>
                    <Select
                      fullWidth
                      displayEmpty
                      value={vehicleType}
                      onChange={(e) => {
                        setVehicleType(e.target.value as number);
                        if (errors.vehicleType)
                          setErrors((prev) => ({ ...prev, vehicleType: undefined }));
                      }}
                      error={!!errors.vehicleType}
                    >
                      <MenuItem value="" disabled>
                        Select Vehicle Type
                      </MenuItem>
                      {Object.keys(VEHICLE_TYPE)
                        .filter((k) => isNaN(Number(k)))
                        .map((key) => (
                          <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                            {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                          </MenuItem>
                        ))}
                    </Select>
                    {errors.vehicleType && (
                      <h6 className="err_msg">{errors.vehicleType}</h6>
                    )}
                  </>
                )}
              </div>

              <div className="control_group">
                <label>Category</label>
                {isEdit ? (
                  <TextField
                    fullWidth
                    hiddenLabel
                    value={categoryName}
                    disabled
                  />
                ) : (
                  <>
                    <Select
                      fullWidth
                      displayEmpty
                      value={categoryId}
                      onChange={(e) => {
                        setCategoryId(e.target.value as string);
                        if (errors.categoryId)
                          setErrors((prev) => ({ ...prev, categoryId: undefined }));
                      }}
                      error={!!errors.categoryId}
                      disabled={!vehicleType}
                    >
                      <MenuItem value="" disabled>
                        {vehicleType ? "Select Category" : "Select vehicle type first"}
                      </MenuItem>
                      {categories.map((cat: any) => (
                        <MenuItem key={cat._id} value={cat._id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.categoryId && (
                      <h6 className="err_msg">{errors.categoryId}</h6>
                    )}
                  </>
                )}
              </div>

              <div className="control_group">
                <label>Banner Image</label>
                <div
                  className="upload_image no_radius v2 mb_10"
                  style={{ width: "100%", height: "150px" }}
                >
                  <label
                    htmlFor="category-banner-image-upload"
                    style={{
                      cursor: isUploading ? "not-allowed" : "pointer",
                      width: "100%",
                      height: "100%",
                      display: "block",
                    }}
                  >
                    <input
                      id="category-banner-image-upload"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      disabled={isUploading}
                      onChange={handleImageUpload}
                    />
                    <div
                      className="upload_image_holder"
                      style={{ width: "100%", height: "100%", position: "relative" }}
                    >
                      {isUploading ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                          }}
                        >
                          <CircularProgress size={24} />
                        </div>
                      ) : image ? (
                        <figure style={{ width: "100%", height: "100%", margin: 0 }}>
                          <CardMedia
                            component="img"
                            image={image}
                            style={{ height: "100%", width: "100%", objectFit: "contain" }}
                          />
                          <IconButton
                            className="remove_img"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setImage("");
                            }}
                            style={{
                              position: "absolute",
                              top: "5px",
                              right: "5px",
                              background: "rgba(255,255,255,0.7)",
                              padding: "4px",
                            }}
                          >
                            <CloseIcon style={{ fontSize: "18px" }} />
                          </IconButton>
                        </figure>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                          }}
                        >
                          <img
                            src="/static/images/placeholder.png"
                            alt=""
                            style={{ width: "40px", marginBottom: "10px" }}
                          />
                          <span>Click to upload banner image</span>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
                {errors.image && <h6 className="err_msg">{errors.image}</h6>}
              </div>

              <div className="footer_btn_flex">
                <Button
                  className="btn btn_grey"
                  onClick={() => {
                    setErrors({});
                    setOpen(false);
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  className="btn btn_primary"
                  disabled={isUploading || isSubmitting}
                  onClick={handleSave}
                >
                  {isSubmitting
                    ? isEdit
                      ? "Updating..."
                      : "Saving..."
                    : isEdit
                    ? "Update"
                    : "Save"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Modal>
  );
}
